import React, { useState, useEffect } from "react";
import API from "../../services/api.ts";
import { toast } from "react-toastify";

const ViewerFormModal = ({ viewer, onClose, onSuccess }) => {
    const isEditing = !!viewer;

    const [formData, setFormData] = useState({
        name: "",
        description: "",
        viewer_type: "ohif",
        supported_data_types: "dicom",
        custom_url: "",
        icon: "fa-desktop",
        color: "#007bff",
        is_active: true,
    });
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState({});

    const viewerTypes = [
        { value: "ohif", label: "OHIF Basic Viewer" },
        { value: "ohif-segmentation", label: "OHIF Segmentation" },
        { value: "ohif-3d", label: "OHIF 3D" },
        { value: "ohif-microscopy", label: "OHIF Microscopy" },
        { value: "kitware-volview", label: "Kitware VolView" },
        { value: "stone-web-viewer", label: "Stone Web Viewer" },
        { value: "dv2", label: "DV2 Multimodal Viewer" },
        { value: "discovery-viewer-test", label: "Discovery Viewer Test" },
        { value: "image-viewer", label: "Image Viewer" },
        { value: "ehr-viewer", label: "EHR Viewer" },
        { value: "signal-viewer", label: "Signal Viewer" },
        { value: "pme", label: "PME Pathology Viewer" },
        { value: "trame-slicer", label: "3D Slicer" },
        { value: "custom", label: "Custom Viewer" },
    ];

    const dataTypes = [
        { value: "dicom", label: "DICOM Only" },
        { value: "image", label: "Images Only" },
        { value: "document", label: "Documents Only" },
        { value: "ehr", label: "EHR Only" },
        { value: "signal", label: "Signals Only" },
        { value: "pathology", label: "Pathology (WSI)" },
        { value: "multimodal", label: "Multimodal (All Types)" },
    ];

    const predefinedColors = [
        "#007bff", "#28a745", "#dc3545", "#ffc107",
        "#17a2b8", "#6f42c1", "#fd7e14", "#20c997"
    ];

    const iconOptions = [
        { value: "fa-desktop", label: "Desktop" },
        { value: "fa-eye", label: "Eye" },
        { value: "fa-image", label: "Image" },
        { value: "fa-cube", label: "Cube" },
        { value: "fa-microscope", label: "Microscope" },
        { value: "fa-cut", label: "Scissors" },
        { value: "fa-flask", label: "Flask" },
        { value: "fa-cog", label: "Cog" },
    ];

    useEffect(() => {
        if (viewer) {
            setFormData({
                name: viewer.name || "",
                description: viewer.description || "",
                viewer_type: viewer.viewer_type || "ohif",
                supported_data_types: viewer.supported_data_types || "dicom",
                custom_url: viewer.custom_url || "",
                icon: viewer.icon || "fa-desktop",
                color: viewer.color || "#007bff",
                is_active: viewer.is_active !== undefined ? viewer.is_active : true,
            });
        }
    }, [viewer]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === "checkbox" ? checked : value
        }));
        // Clear error when field is changed
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: null }));
        }
    };

    const validate = () => {
        const newErrors = {};
        if (!formData.name.trim()) {
            newErrors.name = "Name is required";
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validate()) return;

        setLoading(true);
        try {
            if (isEditing) {
                // Only send editable fields when updating
                const updateData = {
                    name: formData.name,
                    description: formData.description,
                    icon: formData.icon,
                    color: formData.color,
                    is_active: formData.is_active,
                };
                await API.patch(`/viewers/${viewer.id}/`, updateData);
                toast.success("Viewer updated successfully!");
            } else {
                // Send all fields when creating (except custom_url which is removed)
                const createData = {
                    name: formData.name,
                    description: formData.description,
                    viewer_type: formData.viewer_type,
                    supported_data_types: formData.supported_data_types,
                    icon: formData.icon,
                    color: formData.color,
                    is_active: formData.is_active,
                };
                await API.post("/viewers/", createData);
                toast.success("Viewer created successfully!");
            }
            onSuccess();
        } catch (err) {
            console.error("Error saving viewer:", err);
            toast.error(isEditing ? "Failed to update viewer." : "Failed to create viewer.");
        } finally {
            setLoading(false);
        }
    };

    const handleBackdropClick = (e) => {
        if (e.target === e.currentTarget) {
            onClose();
        }
    };

    return (
        <div className="modal-backdrop-custom" onClick={handleBackdropClick}>
            <form className="modal-content-custom" style={{ maxWidth: "550px" }} onSubmit={handleSubmit}>
                <div className="modal-header-custom">
                    <h3>{isEditing ? "Edit Viewer" : "Create New Viewer"}</h3>
                    <button type="button" className="modal-close-btn" onClick={onClose}>
                        &times;
                    </button>
                </div>

                <div className="modal-body-custom">
                        {/* Name */}
                        <div className="form-group mb-3">
                            <label className="form-label">
                                Name <span className="text-danger">*</span>
                            </label>
                            <input
                                type="text"
                                className={`form-control ${errors.name ? "is-invalid" : ""}`}
                                name="name"
                                value={formData.name}
                                onChange={handleChange}
                                placeholder="Enter viewer name"
                            />
                            {errors.name && (
                                <div className="invalid-feedback">{errors.name}</div>
                            )}
                        </div>

                        {/* Description */}
                        <div className="form-group mb-3">
                            <label className="form-label">Description</label>
                            <textarea
                                className="form-control"
                                name="description"
                                value={formData.description}
                                onChange={handleChange}
                                placeholder="Enter description (optional)"
                                rows={2}
                            />
                        </div>

                        {/* Viewer Type */}
                        <div className="form-group mb-3">
                            <label className="form-label">Viewer Type</label>
                            {isEditing ? (
                                <>
                                    <input
                                        type="text"
                                        className="form-control"
                                        value={viewerTypes.find(t => t.value === formData.viewer_type)?.label || formData.viewer_type}
                                        disabled
                                        readOnly
                                    />
                                    <small className="text-muted">Viewer type cannot be changed after creation</small>
                                </>
                            ) : (
                                <select
                                    className="form-control"
                                    name="viewer_type"
                                    value={formData.viewer_type}
                                    onChange={handleChange}
                                >
                                    {viewerTypes.filter(t => t.value !== "custom").map(type => (
                                        <option key={type.value} value={type.value}>
                                            {type.label}
                                        </option>
                                    ))}
                                </select>
                            )}
                        </div>

                        {/* Supported Data Types */}
                        <div className="form-group mb-3">
                            <label className="form-label">Supported Data Types</label>
                            {isEditing ? (
                                <>
                                    <input
                                        type="text"
                                        className="form-control"
                                        value={dataTypes.find(t => t.value === formData.supported_data_types)?.label || formData.supported_data_types}
                                        disabled
                                        readOnly
                                    />
                                    <small className="text-muted">Data types cannot be changed after creation</small>
                                </>
                            ) : (
                                <select
                                    className="form-control"
                                    name="supported_data_types"
                                    value={formData.supported_data_types}
                                    onChange={handleChange}
                                >
                                    {dataTypes.map(type => (
                                        <option key={type.value} value={type.value}>
                                            {type.label}
                                        </option>
                                    ))}
                                </select>
                            )}
                        </div>

                        {/* Icon and Color Row */}
                        <div className="row">
                            <div className="col-md-6">
                                <div className="form-group mb-3">
                                    <label className="form-label">Icon</label>
                                    <select
                                        className="form-control"
                                        name="icon"
                                        value={formData.icon}
                                        onChange={handleChange}
                                    >
                                        {iconOptions.map(icon => (
                                            <option key={icon.value} value={icon.value}>
                                                {icon.label}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                            <div className="col-md-6">
                                <div className="form-group mb-3">
                                    <label className="form-label">Color</label>
                                    <div className="d-flex gap-2 flex-wrap">
                                        {predefinedColors.map(color => (
                                            <button
                                                key={color}
                                                type="button"
                                                className={`btn btn-sm ${formData.color === color ? "ring-2" : ""}`}
                                                style={{
                                                    backgroundColor: color,
                                                    width: "28px",
                                                    height: "28px",
                                                    borderRadius: "4px",
                                                    border: formData.color === color ? "2px solid #000" : "1px solid #dee2e6"
                                                }}
                                                onClick={() => setFormData(prev => ({ ...prev, color }))}
                                            />
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Preview */}
                        <div className="form-group mb-3">
                            <label className="form-label">Preview</label>
                            <div className="p-3 bg-light rounded d-flex align-items-center gap-3">
                                <i
                                    className={`fa ${formData.icon}`}
                                    style={{ fontSize: "1.5rem", color: formData.color }}
                                ></i>
                                <div>
                                    <strong>{formData.name || "Viewer Name"}</strong>
                                    <div className="text-muted small">
                                        {viewerTypes.find(t => t.value === formData.viewer_type)?.label}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Active Checkbox */}
                        <div className="form-check mb-3">
                            <input
                                type="checkbox"
                                className="form-check-input"
                                id="is_active"
                                name="is_active"
                                checked={formData.is_active}
                                onChange={handleChange}
                            />
                            <label className="form-check-label" htmlFor="is_active">
                                Viewer is active
                            </label>
                        </div>
                    </div>

                <div className="modal-footer-custom">
                    <button
                        type="button"
                        className="btn-secondary-custom"
                        onClick={onClose}
                        disabled={loading}
                    >
                        Cancel
                    </button>
                    <button
                        type="submit"
                        className="btn-primary-custom"
                        disabled={loading}
                    >
                        {loading ? (
                            <>
                                <span className="spinner-border spinner-border-sm me-2" />
                                {isEditing ? "Updating..." : "Creating..."}
                            </>
                        ) : (
                            isEditing ? "Update Viewer" : "Create Viewer"
                        )}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default ViewerFormModal;
